import { WidgetTheme } from '../types'

interface ChatBubbleProps {
  theme: WidgetTheme
  onClick: () => void
  isOpen: boolean
  unreadCount?: number
}

export function createChatBubble({ theme, onClick, isOpen, unreadCount = 0 }: ChatBubbleProps): HTMLElement {
  const button = document.createElement('button')
  button.className = 'chat-bubble'
  
  // Apply improved styles
  Object.assign(button.style, {
    background: `linear-gradient(135deg, ${theme.primaryColor} 0%, ${adjustBrightness(theme.primaryColor, -10)} 100%)`,
    color: '#ffffff',
    border: 'none',
    borderRadius: '50%',
    width: '64px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: `0 8px 24px ${theme.primaryColor}25, 0 4px 12px rgba(0, 0, 0, 0.15)`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    outline: 'none',
    backdropFilter: 'blur(10px)',
    animation: isOpen ? '' : 'bubblePulse 3s ease-in-out infinite',
  })

  // Helper function to adjust color brightness
  function adjustBrightness(hex: string, percent: number): string {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  }

  // Enhanced hover effects
  button.addEventListener('mouseenter', () => {
    button.style.transform = 'scale(1.1)'
    button.style.boxShadow = `0 12px 32px ${theme.primaryColor}35, 0 6px 16px rgba(0, 0, 0, 0.2)`
  })
  
  button.addEventListener('mouseleave', () => {
    button.style.transform = 'scale(1)'
    button.style.boxShadow = `0 8px 24px ${theme.primaryColor}25, 0 4px 12px rgba(0, 0, 0, 0.15)`
  })

  // Add click handler with animation
  button.addEventListener('click', () => {
    button.style.animation = 'bubbleClick 0.2s ease'
    setTimeout(() => {
      button.style.animation = isOpen ? '' : 'bubblePulse 3s ease-in-out infinite'
    }, 200)
    onClick()
  })

  // Enhanced icons with better design
  const iconSvg = isOpen 
    ? `<svg width="26" height="26" viewBox="0 0 24 24" fill="none">
         <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
       </svg>`
    : `<svg width="26" height="26" viewBox="0 0 24 24" fill="none">
         <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
       </svg>`

  button.innerHTML = iconSvg

  // Enhanced unread count badge
  if (!isOpen && unreadCount > 0) {
    const badge = document.createElement('div')
    Object.assign(badge.style, {
      position: 'absolute',
      top: '-4px',
      right: '-4px',
      backgroundColor: '#ef4444',
      color: '#ffffff',
      borderRadius: '50%',
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: '700',
      boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4), 0 0 0 2px white',
      animation: 'badgeBounce 0.6s ease-out',
    })
    badge.textContent = unreadCount > 99 ? '99+' : unreadCount.toString()
    button.appendChild(badge)
  }

  // Add CSS animations
  const bubbleAnimationStyle = document.createElement('style')
  bubbleAnimationStyle.textContent = `
    @keyframes bubblePulse {
      0% { 
        box-shadow: 0 8px 24px ${theme.primaryColor}25, 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 0 ${theme.primaryColor}40;
      }
      50% { 
        box-shadow: 0 8px 24px ${theme.primaryColor}35, 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 8px ${theme.primaryColor}20;
      }
      100% { 
        box-shadow: 0 8px 24px ${theme.primaryColor}25, 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 0 ${theme.primaryColor}40;
      }
    }
    
    @keyframes bubbleClick {
      0% { transform: scale(1); }
      50% { transform: scale(0.95); }
      100% { transform: scale(1); }
    }
    
    @keyframes badgeBounce {
      0% { transform: scale(0); }
      60% { transform: scale(1.2); }
      100% { transform: scale(1); }
    }
  `
  if (!document.querySelector('#bubble-animations')) {
    bubbleAnimationStyle.id = 'bubble-animations'
    document.head.appendChild(bubbleAnimationStyle)
  }

  return button}
